import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Lock, LogOut, Clock, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const IDLE_WARNING_MS = 10 * 60 * 1000; // 10 minutes
const IDLE_LOCK_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export function ActivityMonitor() {
  const { user, isAuthenticated, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [idleMinutes, setIdleMinutes] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const lockTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleMinutes(0);
    if (!isLocked) {
      setShowWarning(false);
    }
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    if (isAuthenticated && !isLocked) {
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        setIdleMinutes(10);
        // Send stagnancy email
        _sendStagnancyEmail();
        // Start countdown to lock
        countdownRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 60000);
          setIdleMinutes(elapsed);
        }, 60000);
      }, IDLE_WARNING_MS);

      lockTimerRef.current = setTimeout(() => {
        setIsLocked(true);
        setShowWarning(false);
      }, IDLE_LOCK_MS);
    }
  }, [isAuthenticated, isLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const _sendStagnancyEmail = async () => {
    if (!user?.email) return;
    try {
      await axios.post(`${API}/api/auth/stagnancy-alert`, {
        email: user.email,
        idle_minutes: 10,
      });
    } catch { /* silent */ }
  };

  const handleActivity = useCallback(() => {
    if (!isLocked) resetTimers();
  }, [isLocked, resetTimers]);

  useEffect(() => {
    if (!isAuthenticated) return;
    ACTIVITY_EVENTS.forEach(event => document.addEventListener(event, handleActivity, { passive: true }));
    resetTimers();
    return () => {
      ACTIVITY_EVENTS.forEach(event => document.removeEventListener(event, handleActivity));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isAuthenticated, handleActivity, resetTimers]);

  const handleRemoteLock = () => {
    setIsLocked(true);
    setShowWarning(false);
  };

  const handleUnlock = () => {
    setIsLocked(false);
    resetTimers();
  };

  const handleLogout = () => {
    setIsLocked(false);
    setShowWarning(false);
    logout();
  };

  const handleContinue = () => {
    setShowWarning(false);
    resetTimers();
  };

  if (!isAuthenticated) return null;

  // Lock Screen
  if (isLocked) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900/95 backdrop-blur-sm flex items-center justify-center" data-testid="lock-screen">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Screen Locked</h2>
          <p className="text-sm text-gray-500 mb-1">Your session was locked due to inactivity.</p>
          <p className="text-xs text-gray-400 mb-6">Your work is saved. Unlock to continue or logout.</p>
          <div className="space-y-2">
            <Button className="w-full" onClick={handleUnlock} data-testid="unlock-btn">
              <Shield className="h-4 w-4 mr-2" />Unlock & Continue
            </Button>
            <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout} data-testid="lock-logout-btn">
              <LogOut className="h-4 w-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Idle Warning
  return (
    <Dialog open={showWarning} onOpenChange={(v) => { if (!v) handleContinue(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-amber-500" />Still there?</DialogTitle>
          <DialogDescription>You've been inactive for {idleMinutes} minutes. An alert email has been sent to your inbox.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-gray-500">Your session will auto-lock after 30 minutes of inactivity. All your work is saved.</p>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={handleContinue} data-testid="continue-btn">I'm Here</Button>
            <Button variant="outline" className="flex-1" onClick={handleRemoteLock} data-testid="remote-lock-btn">
              <Lock className="h-3.5 w-3.5 mr-1" />Lock Screen
            </Button>
          </div>
          <Button variant="ghost" className="w-full text-xs text-red-500" onClick={handleLogout}>Logout Now</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
